import React, { useRef, useEffect, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';

export interface SignaturePadProps {
  value: string;
  onChange: (dataUrl: string) => void;
  height?: number;
  borderColor?: string;
  clearButtonColor?: string;
}

/**
 * Signature canvas with correct pointer ↔ pixel mapping.
 * Internal buffer size tracks the displayed size (and devicePixelRatio for sharp strokes).
 */
const SignaturePad: React.FC<SignaturePadProps> = ({
  value,
  onChange,
  height = 160,
  borderColor = '#e2e8f0',
  clearButtonColor,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const cssW = Math.max(1, Math.floor(rect.width));
    const cssH = Math.max(1, Math.floor(height));
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 2.5;
    }
  }, [height]);

  useEffect(() => {
    syncCanvasSize();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      syncCanvasSize();
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [syncCanvasSize]);

  const getCssPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const emitChange = () => {
    const canvas = canvasRef.current;
    if (canvas) onChangeRef.current(canvas.toDataURL('image/png'));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawing.current = true;
    lastPos.current = getCssPoint(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !canvasRef.current || !lastPos.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const pos = getCssPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    emitChange();
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    try {
      canvasRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      // ignore if capture was already released
    }
    drawing.current = false;
    lastPos.current = null;
    emitChange();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.width / dpr;
    const cssH = canvas.height / dpr;
    ctx.clearRect(0, 0, cssW, cssH);
    onChangeRef.current('');
  };

  return (
    <Box>
      <Box
        ref={containerRef}
        sx={{
          border: '1.5px solid',
          borderColor,
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: '#fafafa',
          touchAction: 'none',
          width: '100%',
          height,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            cursor: 'crosshair',
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Sign above with your mouse or finger
        </Typography>
        <Button
          size="small"
          onClick={clear}
          disabled={!value}
          sx={{ textTransform: 'none', ...(clearButtonColor ? { color: clearButtonColor, fontWeight: 600 } : {}) }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default SignaturePad;
